/**
 * polylineThicknessSweep — the DXF group-39 THICKNESS of a 2D polyline, drawn.
 *
 * # The defect this closes — @codex PR #785 round 15, FINDING 3
 *
 * Group 39 is not a property; it is GEOMETRY. Autodesk's DXF reference
 * ("Thickness (39)" + "Object Coordinate Systems (OCS)") defines it as the
 * EXTRUSION of the entity along its extrusion direction, producing a 3D
 * surface, and `cad-core`'s `heavyPolylineDrawnBounds` has indexed exactly that
 * surface since round 9: the drawn silhouette UNION its copy translated by
 * `t·N̂`. The canvas did not. With an OBLIQUE group-210 normal the omitted half
 * has real XY extent — extrusion `(1, 1, 1)`, thickness 5, constant width 4 on
 * `(0,0) → (10,0)` puts the drawn `maxX` at 3.7032479268758554 while the
 * unswept band reaches only 0.8164965809277261 — so the spatial index and the
 * selection box described geometry the canvas never painted. That is the same
 * index-vs-canvas disagreement rounds 8–12 closed in the OTHER direction (a box
 * SMALLER than the paint, which drops visible entities), and per CLAUDE.md
 * ПРИОРИТЕТ #1 the parity answer is to paint it, not to shrink the box.
 *
 * NOTHING in this repo rendered group 39 for ANY primitive before this module
 * (LINE, POINT, CIRCLE, ARC, LWPOLYLINE, TEXT and the heavy POLYLINE all
 * carried the field through parse / store / property panel and dropped it at
 * the adapter), so there was no existing path to reuse — the module doc says so
 * rather than leaving the next reader to re-run the search. The heavy POLYLINE
 * is done here because it is the entity whose bounds now CLAIM the sweep; the
 * other kinds' bounds claim it too (`drawing-model/wcsBoundsLeaf.ts`'
 * `lineLocal` / `pointLocal` / `ellipseLocal` / the LWPOLYLINE local walk) and
 * are a named follow-up, not a silent gap — each is the same two lines in its
 * own adapter once this shape exists.
 *
 * # WHICH subtypes are swept, and why only those
 *
 * The 2D polyline ONLY. Autodesk's OCS section lists 3DFACE, 3DPOLYLINE,
 * 3DVERTEX, MESH, LINE, POINT and VIEWPORT as the entities that "do not lie in
 * a particular plane", and states that "of these entities, only lines and
 * points can be extruded". A 3D polyline, a polygon mesh and a poly-face mesh
 * therefore have no thickness surface at all, and fabricating one would be
 * geometry the drawing does not contain. (`entityModelBounds` DOES sweep those
 * subtypes — an over-report in the safe direction, pre-existing and unchanged
 * here.)
 *
 * # WHAT is drawn — base, top, and the rulings
 *
 * The swept surface is RULED: every point `p` of the drawn base outline is
 * joined to `p + t·N̂`. AutoCAD's 2D-wireframe display of an extruded polyline
 * shows the base outline, the top outline, and the rulings at the outline's
 * CORNERS — not at every point of a smooth arc. So:
 *
 *   - the TOP outline is the base vertex stream translated by the sweep vector,
 *     which is exact for any base (a translation commutes with tessellation);
 *   - the RULINGS are taken at the caller-supplied anchors:
 *       * a WIDTH-LESS run anchors them at the AUTHORED vertices
 *         (`geometry.points`), which is the AutoCAD wireframe exactly — a
 *         bulged span gets rulings at its two ends and nothing along the bow;
 *       * a BAND anchors them at the band loops' OWN vertices, because the
 *         centreline is INSIDE the swept solid and its rulings are not surface
 *         edges at all. DECLARED RESIDUAL, with its exact size: along a BULGED
 *         wide span `widePolylineBandLoops` subdivides the arc, so that span
 *         gets one ruling per subdivision instead of one per end — every one of
 *         them is a genuine ruling OF the surface (nothing is fabricated), but
 *         it is denser than AutoCAD's wireframe. Closing it means having
 *         `widePolylineBandLoops` label which of its loop vertices came from an
 *         AUTHORED station, which is a change to that shared builder and its
 *         plot-side caller.
 *
 * # DECLARED RESIDUALS, named rather than left to be rediscovered
 *
 * Both leave PICKABLE a SUBSET of DRAWN — the invariant `blockSelectGeometry.ts`
 * states and the one `lwpolylineDrawnBounds.ts`' header already records for the
 * LWPOLYLINE picker — so neither makes anything selectable that is not visible.
 * That is NOT the same as harmless: making geometry visible without making it
 * reachable is its own regression, which is why residual 1 now names only the
 * snaps (and says so) instead of the whole interaction surface.
 *
 *   1. THE OBJECT SNAPS DO NOT SEE THE SWEEP — `snap/osnapCandidates.ts`,
 *      `snap/osnapIntersections.ts` and the AI mirror `ai/sceneQuery/osnapQuery.ts`
 *      still read `geometry.points` / `polylineCentrelineSamples` and therefore
 *      offer ENDPOINT / MIDPOINT / INTERSECTION only on the base outline. That
 *      is a live gap, NOT a safe one — the top outline is painted, so a user can
 *      see an edge whose endpoint no snap will find — and it is named here for
 *      routing rather than left to be rediscovered. (THE PICK AND THE WINDOW
 *      SELECT NO LONGER BELONG ON THIS LIST: `raycast/distance.ts` and
 *      `raycast/windowSelect.ts` both consume {@link drawnPolylineSweptRuns},
 *      the same assembly the batcher's stream is a projection of, and
 *      `raycast/thicknessSweptPick.property.test.ts` pins the two
 *      segment-for-segment.)
 *   2. `$FILLMODE` 1 FILLS THE BASE BAND ONLY. `scene/widePolylineFillMesh.ts`
 *      triangulates the band loops; the swept copy is stroked as an OUTLINE and
 *      not filled. AutoCAD shades the extruded faces in a shaded visual style;
 *      this canvas is a wireframe view of them.
 *
 * # COST
 *
 * Paid only by an entity that declares a thickness. `thicknessSweepVector`
 * returns `null` for `|t| < 1e-9` — `drawing-model`'s own gate, reused rather
 * than restated — and {@link withPolylineThicknessSweep} then returns the base
 * stream ITSELF, so the overwhelmingly common thickness-less polyline allocates
 * nothing and compares one number. An entity that does declare one roughly
 * triples its vertex count (base + top + one ruling per anchor), which is what
 * drawing three times as much geometry costs.
 */

import { withThicknessAlongExtrusion } from "@dxf-ai-editor/drawing-model";

import type { Vec3 } from "../../types";

import {
  drawnPolylineBandLoops,
  type DrawnBandGeometry,
} from "./polylineDrawnBand";
import { polylineCentrelineSamples } from "./polylineWidthBand";

/** The geometry fields the sweep reads — structural, so a bare literal fits. */
export interface ThicknessSweepGeometry {
  /** DXF group 39, already resolved into a WORLD vector `t·N̂`. */
  thicknessSweep?: Vec3 | undefined;
}

/**
 * The WORLD vector a group-39 thickness displaces the entity by: `t·N̂`, where
 * `N̂` is the normalized group-210 extrusion (the WCS `ẑ` when it is absent or
 * default). `null` when the entity declares no thickness.
 *
 * The direction is NOT normalized here. `drawing-model`'s
 * {@link withThicknessAlongExtrusion} is the ONE reading of "which way, and how
 * far, does group 39 move this entity" — it is what `wcsBoundsLeaf.ts` and
 * `cad-core/heavyPolylineDrawnBounds.ts` both sweep with, so the canvas must
 * sweep with the SAME call or it re-opens the disagreement this module closes.
 * That function answers in terms of POINTS, so the vector is read off as the
 * image of the ORIGIN: `withThicknessAlongExtrusion([0], t, e)` returns
 * `[origin, origin + t·N̂]`, and the second element IS the vector. Recomputing
 * `computeOcsAxes(extrusion).az` here instead would be a second normalization
 * of the same group code (CLAUDE.md ПРИОРИТЕТ #2.1).
 *
 * `null` rather than a zero vector for the no-thickness case, so the caller's
 * gate is a null check rather than a float comparison it would have to pick a
 * tolerance for: the tolerance is `withThicknessAlongExtrusion`'s own `1e-9`,
 * applied here by observing that it returned the point list UNCHANGED.
 */
export function thicknessSweepVector(
  thickness: number,
  extrusion: Vec3 | undefined,
): Vec3 | null {
  const origin: Vec3 = { x: 0, y: 0, z: 0 };
  const swept = withThicknessAlongExtrusion([origin], thickness, extrusion);
  // Length 1 ⇒ the helper's own `Math.abs(thickness) < 1e-9` gate declined.
  const moved = swept[1];
  return moved === undefined ? null : moved;
}

/**
 * ONE run of the drawn wireframe: a chain of points, closed or open. The shape
 * both projections of the sweep are expressed in — the batcher flattens it to a
 * `LINES` stream, the pick and the window select measure against it directly.
 */
export interface DrawnPolylineRun {
  readonly points: readonly Vec3[];
  readonly closed: boolean;
}

/** What {@link drawnPolylineSweptRuns} answers. */
export interface DrawnPolylineShape {
  /** Every run the canvas strokes: the base, its swept copy, and the rulings. */
  readonly runs: readonly DrawnPolylineRun[];
  /**
   * The BASE band loops — what `$FILLMODE` 1 actually PAINTS SOLID
   * (`scene/widePolylineFillMesh.ts`) — or `null` when the run is stroked as a
   * centreline and nothing is filled. Deliberately NOT the swept copy: residual
   * 2 in the module header says the top face is stroked as an outline and never
   * filled, so a consumer that treats the band's interior as "on the entity"
   * must do so for the base only, or it makes pickable a SUPERSET of drawn.
   */
  readonly bandLoops: Vec3[][] | null;
}

/**
 * THE ONE DERIVATION of the swept surface: `base`, its copy translated by
 * `t·N̂`, and one ruling per anchor.
 *
 * Everything else in this module and every consumer of it is a PROJECTION of
 * this function — {@link withPolylineThicknessSweep} flattens its answer into
 * the batcher's `LINES` stream, {@link drawnPolylineSweptRuns} feeds it the
 * drawn base. A second place that decides "where does the thickness put the top
 * outline" would be the index-vs-canvas disagreement this module exists to
 * close, one level down (CLAUDE.md ПРИОРИТЕТ #2.1).
 *
 * Returns `base` ITSELF when the geometry declares no thickness, so a caller on
 * the hot path pays one property read, and when `base` is EMPTY — a run the
 * canvas paints nothing for cannot acquire rulings out of nowhere.
 *
 * `rulingAnchors` is `null` to mean "every DISTINCT point of `base`" — the
 * BAND case, where the base runs' own vertices are the silhouette's (see the
 * module doc for why the centreline's are not). {@link distinctRunPoints} does
 * the collapsing, and its doc says why that has to be a GLOBAL de-duplication
 * rather than a consecutive one.
 */
export function withPolylineThicknessSweepRuns(
  geometry: ThicknessSweepGeometry,
  base: readonly DrawnPolylineRun[],
  rulingAnchors: readonly Vec3[] | null,
): readonly DrawnPolylineRun[] {
  const sweep = geometry.thicknessSweep;
  if (!sweep) return base;
  if (base.length === 0) return base;

  const out: DrawnPolylineRun[] = [...base];

  // THE TOP OUTLINE. Translating the finished runs is exact for any base — the
  // sweep is a rigid translation, so it commutes with whatever tessellation
  // produced them, and the two outlines are therefore the same curve by
  // construction rather than by two agreeing samplers.
  for (const run of base) {
    out.push({
      points: run.points.map((p) => translate(p, sweep)),
      closed: run.closed,
    });
  }

  // THE RULINGS.
  const anchors = rulingAnchors ?? distinctRunPoints(base);
  for (const a of anchors) {
    out.push({ points: [a, translate(a, sweep)], closed: false });
  }

  return out;
}

/** `p + sweep`. The single statement of where the thickness puts a point. */
function translate(p: Vec3, sweep: Vec3): Vec3 {
  return { x: p.x + sweep.x, y: p.y + sweep.y, z: p.z + sweep.z };
}

/**
 * The DISTINCT points of a run list, first occurrence kept, in order.
 *
 * De-duplication is GLOBAL, not consecutive-only, and that is load-bearing
 * rather than tidiness: the same base reaches this function in two shapes —
 * whole rings from {@link drawnPolylineSweptRuns}, one run per SEGMENT from
 * {@link withPolylineThicknessSweep}'s stream split — and a consecutive-only
 * pass answers differently for the two. A closed ring's stream repeats its first
 * vertex at the end of the closing segment, which is not adjacent to its first
 * occurrence, so the stream grew one extra ruling exactly on top of an identical
 * one while the ring shape grew none. Identical geometry, two different vertex
 * counts: the canvas-vs-interaction disagreement this module exists to prevent,
 * in miniature. Keyed rather than compared pairwise so a long silhouette costs
 * one pass. (`-0` keys as `0`; it is the same point, and which of the two
 * objects is kept cannot matter because the ruling is built from its numbers.)
 */
function distinctRunPoints(runs: readonly DrawnPolylineRun[]): Vec3[] {
  const out: Vec3[] = [];
  const seen = new Set<string>();
  for (const run of runs) {
    for (const p of run.points) {
      const key = `${p.x},${p.y},${p.z}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(p);
    }
  }
  return out;
}

/**
 * {@link withPolylineThicknessSweepRuns} projected into the batcher's flat
 * `LINES`-mode vertex stream (consecutive triples are points, consecutive point
 * PAIRS are segments).
 *
 * A `LINES` stream IS a list of independent 2-point runs, so the projection is
 * exact in both directions and this adds no second reading of the sweep: the
 * stream is split into its segments, swept by the ONE derivation, and written
 * back out in the same order — base segments, then the translated copy, then the
 * rulings.
 *
 * The one thing that is NOT identical to the direct stream walk this replaced:
 * an anchor repeated in the stream now yields ONE ruling instead of two. A
 * closed ring's stream ends by repeating its first vertex, and the old
 * consecutive-only de-duplication could not see that it had already been
 * anchored, so it emitted a second ruling exactly on top of the first —
 * invisible on screen, but a vertex count the run-shaped consumers could never
 * match. {@link distinctRunPoints} carries the argument.
 *
 * Returns `base` ITSELF when the geometry declares no thickness.
 */
export function withPolylineThicknessSweep(
  geometry: ThicknessSweepGeometry,
  base: number[],
  rulingAnchors: readonly Vec3[] | null,
): number[] {
  if (!geometry.thicknessSweep) return base;
  if (base.length === 0) return base;

  const swept = withPolylineThicknessSweepRuns(
    geometry,
    streamSegmentRuns(base),
    rulingAnchors,
  );

  const out: number[] = [];
  for (const run of swept) {
    for (const p of run.points) out.push(p.x, p.y, p.z);
  }
  return out;
}

/**
 * A `LINES` stream as one open run per SEGMENT. A trailing lone point (a
 * malformed stream) becomes a one-point run, which flattens back to the same
 * three numbers rather than being silently dropped.
 */
function streamSegmentRuns(stream: readonly number[]): DrawnPolylineRun[] {
  const out: DrawnPolylineRun[] = [];
  for (let i = 0; i + 2 < stream.length; i += 6) {
    const a: Vec3 = { x: stream[i]!, y: stream[i + 1]!, z: stream[i + 2]! };
    const bx = stream[i + 3];
    if (bx === undefined) {
      out.push({ points: [a], closed: false });
      break;
    }
    out.push({
      points: [a, { x: bx, y: stream[i + 4]!, z: stream[i + 5]! }],
      closed: false,
    });
  }
  return out;
}

/**
 * The runs a polyline is actually DRAWN as when that shape is something an
 * analytic centreline walk cannot express — a width BAND, a group-39 SWEEP, or
 * both — or `null` when the entity is a plain stroked centreline whose caller
 * already measures it exactly.
 *
 * # Why the pick and the window select call THIS
 *
 * `batchedLayer.ts::geometryToLineVertices`' `"polyline"` arm is the same three
 * decisions in the same order — band or centreline, which anchors the rulings
 * take, then the sweep — and its stream is this function's answer flattened by
 * {@link withPolylineThicknessSweep}. `raycast/thicknessSweptPick.property.test.ts`
 * asserts that equality SEGMENT-FOR-SEGMENT, in order and to the exact float,
 * over generated runs, extrusions and thicknesses — so the two cannot drift:
 * whatever the canvas paints is what the pointer measures and what a crossing
 * rectangle tests.
 *
 * That gate is the point. @codex PR #785 round 16 caught the half-fix — round 15
 * made the batcher paint the translated top outline and the rulings while
 * `raycast/distance.ts` and `raycast/windowSelect.ts` went on testing the
 * unswept base, so with an oblique group-210 normal a user could SEE an edge,
 * click exactly on it, and select nothing (extrusion `(1,1,1)`, thickness 5,
 * width 4 on `(0,0) → (10,0)`: the painted top vertex at
 * `(3.7032479268758554, 3.7032479268758554)` measured 4.08248290463863 against a
 * 4-px aperture of 0.08 world units, and the entity's own bounds contained it
 * the whole time). Making geometry visible without making it reachable is a
 * regression introduced by the fix, not an inherited limitation.
 *
 * # `null`, and why it is not "an empty shape"
 *
 * A width-less, thickness-less polyline is EXACTLY the centreline its callers
 * already measure — `raycast/distance.ts` against analytic bulge arcs when the
 * extrusion is default, `raycast/windowSelect.ts` against
 * `polylineCentrelineSamples`' input array itself — and re-expressing it as a
 * run here would swap an exact arc for its inscribed chords on the overwhelming
 * majority of entities. `null` means "nothing here the caller does not already
 * do", so the common path stays byte-identical and allocates nothing.
 *
 * A run the canvas paints NOTHING for (fewer than two centreline samples, i.e.
 * a single-vertex polyline) also answers `null`: it has no drawn edge for the
 * sweep to rule from, and {@link withPolylineThicknessSweepRuns} would otherwise
 * hand back rulings the batcher never emits — pickable outside drawn, the
 * direction this whole round is about.
 */
export function drawnPolylineSweptRuns(
  geometry: DrawnBandGeometry & ThicknessSweepGeometry,
): DrawnPolylineShape | null {
  const bandLoops = drawnPolylineBandLoops(geometry);
  if (bandLoops) {
    return {
      // The band's OWN silhouette vertices anchor the rulings (`null`), because
      // the centreline is inside the swept solid — the module doc's rule, and
      // the same argument the batcher passes.
      runs: withPolylineThicknessSweepRuns(
        geometry,
        bandLoops.map((loop) => ({ points: loop, closed: true })),
        null,
      ),
      bandLoops,
    };
  }
  if (!geometry.thicknessSweep) return null;

  const samples = polylineCentrelineSamples(
    geometry.points,
    geometry.closed,
    geometry.bulges,
    geometry.extrusion,
  );
  if (samples.length < 2) return null;
  return {
    // A WIDTH-LESS run anchors its rulings at the AUTHORED vertices, which is
    // AutoCAD's wireframe exactly — again the batcher's own argument.
    runs: withPolylineThicknessSweepRuns(
      geometry,
      [{ points: samples, closed: geometry.closed }],
      geometry.points,
    ),
    bandLoops: null,
  };
}
